import { create } from "zustand";
import { storageService } from "../services/storageService";
import type { RecordingFile } from "../types/models";
import type { AvailableLanguage } from "../utils/i18n";
import { detectBrowserLanguage } from "../utils/languageUtils";

// index.html の初期化スクリプトと同じ優先順位でダークモード初期値を決定する
// (localStorage.theme → OS の prefers-color-scheme)
const getInitialDarkMode = (): boolean => {
	try {
		const theme = localStorage.getItem("theme");
		if (theme === "dark") return true;
		if (theme === "light") return false;
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	} catch {
		return false;
	}
};

// <html> に dark/light クラスを反映する（Tailwind darkMode: "class" が参照する）
const applyThemeClass = (dark: boolean) => {
	const root = document.documentElement;
	root.classList.toggle("dark", dark);
	root.classList.toggle("light", !dark);
};

interface AppState {
	// SpeechRecognition API availability
	isSpeechRecognitionAvailable: boolean;
	setIsSpeechRecognitionAvailable: (isAvailable: boolean) => void;
	
	// Unsupported mobile browser check
	isUnsupportedMobileBrowser: boolean;
	setIsUnsupportedMobileBrowser: (isUnsupported: boolean) => void;
	
	// Recording state
	isRecording: boolean;
	setIsRecording: (isRecording: boolean) => void;

	// Microphone settings
	selectedMicrophoneId: string;
	setSelectedMicrophoneId: (deviceId: string) => void;

	// Microphone permission & devices (audioStreamService が更新する)
	micPermission: "unknown" | "pending" | "granted" | "denied";
	setMicPermission: (permission: "unknown" | "pending" | "granted" | "denied") => void;
	audioDevices: { deviceId: string; label: string }[];
	setAudioDevices: (devices: { deviceId: string; label: string }[]) => void;

	// Transcription text
	transcriptionText: string;
	appendTranscriptionText: (text: string) => void;
	resetTranscriptionText: () => void;

	// Interim transcription text (real-time recognition candidates)
	interimText: string;
	setInterimText: (text: string) => void;
	
	// Speech recognition state
	isListening: boolean;
	setIsListening: (isListening: boolean) => void;
	recognitionError: string | null;
	setRecognitionError: (message: string | null) => void;
	
	// Speech recognition language settings (for audio recognition, not UI)
	selectedLanguage: string;
	setSelectedLanguage: (language: string) => void;
	availableLanguages: { code: string; name: string }[];

	// UI language settings
	uiLanguage: AvailableLanguage;
	setUILanguage: (language: AvailableLanguage) => void;

	// Dark mode (persisted to localStorage "theme"; index.html applies it before first paint)
	darkMode: boolean;
	setDarkMode: (dark: boolean) => void;

	// Past recordings
	recordings: RecordingFile[];
	/** 録音を追加して生成された id を返す（IndexedDB へも保存） */
	addRecording: (recording: Omit<RecordingFile, "id">) => string;
	/** 既存の録音を id で更新（IndexedDB へも保存） */
	updateRecording: (id: string, patch: Partial<Omit<RecordingFile, "id">>) => void;
	deleteRecording: (id: string) => void;
	clearRecordings: () => void;
	// Storage persistence
	isLoadingRecordings: boolean;
	hasLoadedRecordings: boolean;
	loadRecordingsFromStorage: () => Promise<void>;
}

export const useAppStore = create<AppState>((set) => ({
	// SpeechRecognition API availability
	isSpeechRecognitionAvailable: false,
	setIsSpeechRecognitionAvailable: (isAvailable) => set({ isSpeechRecognitionAvailable: isAvailable }),
	
	// Unsupported mobile browser check
	isUnsupportedMobileBrowser: false,
	setIsUnsupportedMobileBrowser: (isUnsupported) => set({ isUnsupportedMobileBrowser: isUnsupported }),
	
	// Initial recording state
	isRecording: false,
	setIsRecording: (isRecording) => set({ isRecording }),

	// Microphone settings
	selectedMicrophoneId: "",
	setSelectedMicrophoneId: (deviceId) => set({ selectedMicrophoneId: deviceId }),

	// Microphone permission & devices
	micPermission: "unknown",
	setMicPermission: (permission) => set({ micPermission: permission }),
	audioDevices: [],
	setAudioDevices: (devices) => set({ audioDevices: devices }),

	// Initial transcription text
	transcriptionText: "",
	appendTranscriptionText: (text) => set((state) => ({ transcriptionText: state.transcriptionText + text })),
	resetTranscriptionText: () => set({ transcriptionText: "" }),

	// Interim transcription text (real-time candidates)
	interimText: "",
	setInterimText: (text) => set({ interimText: text }),
	
	// Speech recognition state
	isListening: false,
	setIsListening: (isListening) => set({ isListening }),
	recognitionError: null,
	setRecognitionError: (message) => set({ recognitionError: message }),
	// Speech recognition language settings
	selectedLanguage: "ja-JP",
	setSelectedLanguage: (language) => set({ selectedLanguage: language }),
	availableLanguages: [
		{ code: "ja-JP", name: "日本語 (Japanese)" },
		{ code: "en-GB", name: "English (UK)" },
		{ code: "en-US", name: "English (US)" },
		{ code: "de-DE", name: "Deutsch (German)" },
		{ code: "es-ES", name: "Español (Spanish)" },
		{ code: "fr-FR", name: "Français (French)" },
		{ code: "it-IT", name: "Italiano (Italian)" },
		{ code: "ko-KR", name: "한국어 (Korean)" },
    { code: "pt-PT", name: "Português (Portugal)" },
		{ code: "pt-BR", name: "Português (Brazilian)" },
    { code: "ru-RU", name: "Русский (Russian)" },
		{ code: "zh-CN", name: "中文 (中国大陆) Chinese" },
    { code: "zh-TW", name: "中文 (台灣) Chinese (Taiwan)" },
  ],
	
	// UI language settings
	// detectBrowserLanguage が localStorage の保存値を検証してから返す
	// （不正な値が入っていても translations[uiLanguage] が undefined にならない）
	uiLanguage: detectBrowserLanguage(),
	setUILanguage: (language) => {
		localStorage.setItem("uiLanguage", language);
		set({ uiLanguage: language });
	},

	// Dark mode
	darkMode: getInitialDarkMode(),
	setDarkMode: (dark) => {
		try {
			localStorage.setItem("theme", dark ? "dark" : "light");
		} catch {
			// localStorage が使えなくてもトグル自体は機能させる
		}
		applyThemeClass(dark);
		set({ darkMode: dark });
	},
	// Past recordings
	recordings: [],
	isLoadingRecordings: false,
	hasLoadedRecordings: false,
	// IndexedDBから録音データを読み込む
	loadRecordingsFromStorage: async () => {
		// ガードは非同期処理の前に同期的にセットする（多重呼び出しによる並行ロードを防ぐ）
		if (useAppStore.getState().hasLoadedRecordings) return;
		set({ hasLoadedRecordings: true, isLoadingRecordings: true });

		try {
			const savedRecordings = await storageService.getAllRecordings();
			set({
				recordings: savedRecordings,
				isLoadingRecordings: false,
			});
		} catch (error) {
			console.error("Failed to load recordings:", error);
			// 失敗したら再試行できるようにガードを戻す
			set({ hasLoadedRecordings: false, isLoadingRecordings: false });
		}
	},

	// 録音を追加
	addRecording: (recording) => {
		const id = Date.now().toString();
		const newRecording = { ...recording, id };

		// Save to IndexedDB
		storageService
			.saveRecording(newRecording)
			.catch((err) => console.error("Failed to save recording data:", err));

		// 状態を更新
		set((state) => ({
			recordings: [...state.recordings, newRecording],
		}));

		return id;
	},

	// 録音を更新（MP3変換完了時など）
	updateRecording: (id, patch) => {
		let updated: RecordingFile | undefined;
		set((state) => ({
			recordings: state.recordings.map((recording) => {
				if (recording.id !== id) return recording;
				updated = { ...recording, ...patch };
				return updated;
			}),
		}));

		// IndexedDB へは更新後の完全なレコードを保存する
		if (updated) {
			storageService
				.saveRecording(updated)
				.catch((err) => console.error("Failed to update recording data:", err));
		}
	},

	// Delete recording
	deleteRecording: (id: string) => {
		// Delete from IndexedDB
		storageService
			.deleteRecording(id)
			.catch((err) => console.error("Failed to delete recording data:", err));

		// 状態を更新
		set((state) => ({
			recordings: state.recordings.filter((recording) => recording.id !== id),
		}));
	},
	// Clear all recordings
	clearRecordings: () => {
		// Delete all from IndexedDB
		storageService
			.clearAllRecordings()
			.catch((err) => console.error("Failed to clear all recording data:", err));

		// 状態を更新
		set({ recordings: [] });
	},
}));
